import React from 'react';
import { X, HelpCircle, Shield, CheckCircle2, TrendingUp, Clock, Building2, Award, FileCheck } from 'lucide-react';

const ScoreExplainerModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const scoreComponents = [
    {
      name: 'Trust',
      weight: '20%',
      color: '#4F8EF7',
      icon: Shield,
      description: 'Consistency between what you reported and what employers co-signed, across every record.'
    },
    {
      name: 'Performance',
      weight: '25%',
      color: '#00C9A7',
      icon: Award,
      description: 'Weighted average of manager ratings across your published, rated records.'
    },
    {
      name: 'Consistency',
      weight: '10%',
      color: '#D8B45C',
      icon: CheckCircle2,
      description: 'Tenure length and gap patterns across your career history. Improves as more roles are added.'
    },
    {
      name: 'Verification Depth',
      weight: '15%',
      color: '#FF9F6B',
      icon: FileCheck,
      description: 'How many fields per record were corroborated by your employer versus self-reported.'
    },
    {
      name: 'Tenure',
      weight: '10%',
      color: '#4F8EF7',
      icon: Clock,
      description: 'Career length and role progression across your published record history.'
    },
    {
      name: 'Employer Standing',
      weight: '10%',
      color: '#00C9A7',
      icon: Building2,
      description: 'Average tier of the companies that have co-signed your records.'
    },
    {
      name: 'Record Recency',
      weight: '10%',
      color: '#D8B45C',
      icon: TrendingUp,
      description: 'How current your most recently verified record is.'
    }
  ];

  const tiers = [
    { name: 'UNVERIFIED', range: '0-19', color: '#6B7280', description: 'Profile created, verification in progress' },
    { name: 'VERIFIED', range: '20-49', color: '#3B82F6', description: 'Identity confirmed, initial records verified' },
    { name: 'ESTABLISHED', range: '50-74', color: '#8B5CF6', description: 'Multiple verified records with good ratings' },
    { name: 'DISTINGUISHED', range: '75-100', color: '#F59E0B', description: 'Comprehensive verification history, top performers' }
  ];

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="score-explainer-overlay"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      {/* Modal */}
      <div 
        className="relative bg-[#0D0D1A] border border-[#2A2A4A] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="score-explainer-modal"
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#0D0D1A] border-b border-[#2A2A4A] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#4F8EF7] to-[#00C9A7] flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">How Your VETT Score Works</h2>
              <p className="text-sm text-[#8888AA]">Understanding the 7 components</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-[#1A1A2E] hover:bg-[#2A2A4A] flex items-center justify-center transition-colors"
            data-testid="score-explainer-close"
          >
            <X className="w-4 h-4 text-[#8888AA]" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Intro */}
          <div className="bg-[#1A1A2E] rounded-xl p-4">
            <p className="text-sm text-[#CCCCDD] leading-relaxed">
              Your VETT Score (0-100) is a composite measure of your verified employment history. 
              It's calculated from <strong className="text-white">7 weighted components</strong>, 
              each reflecting a different aspect of your professional credibility. The score is 
              <strong className="text-white"> not a performance rating</strong> — it measures how 
              thoroughly your career has been verified and corroborated by employers.
            </p>
          </div>

          {/* Score Components */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <span className="w-5 h-5 rounded bg-[#4F8EF7]/20 flex items-center justify-center text-xs text-[#4F8EF7]">7</span>
              Score Components
            </h3>
            <div className="space-y-3">
              {scoreComponents.map((component, index) => {
                const Icon = component.icon;
                return (
                  <div 
                    key={component.name}
                    className="bg-[#1A1A2E] rounded-xl p-4 border border-[#2A2A4A] hover:border-[#3A3A5A] transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div 
                        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${component.color}20` }}
                      >
                        <Icon className="w-4 h-4" style={{ color: component.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="font-medium text-white text-sm">{component.name}</span>
                          <span 
                            className="text-xs font-mono px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: `${component.color}20`, color: component.color }}
                          >
                            {component.weight}
                          </span>
                        </div>
                        <p className="text-xs text-[#8888AA] leading-relaxed">{component.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tier Levels */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-3">Tier Levels</h3>
            <div className="grid grid-cols-2 gap-3">
              {tiers.map((tier) => (
                <div 
                  key={tier.name}
                  className="bg-[#1A1A2E] rounded-xl p-3 border border-[#2A2A4A]"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span 
                      className="text-xs font-bold px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: `${tier.color}20`, color: tier.color }}
                    >
                      {tier.name}
                    </span>
                    <span className="text-xs font-mono text-[#8888AA]">{tier.range}</span>
                  </div>
                  <p className="text-xs text-[#8888AA]">{tier.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* How to Improve */}
          <div className="bg-gradient-to-br from-[#4F8EF7]/10 to-[#00C9A7]/10 rounded-xl p-4 border border-[#4F8EF7]/20">
            <h3 className="text-sm font-semibold text-white mb-2">How to Improve Your Score</h3>
            <ul className="space-y-2 text-xs text-[#CCCCDD]">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#00C9A7] mt-0.5 flex-shrink-0" />
                <span>Add more verified employment records from past employers</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#00C9A7] mt-0.5 flex-shrink-0" />
                <span>Request manager ratings for existing records</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#00C9A7] mt-0.5 flex-shrink-0" />
                <span>Ensure all record fields are corroborated by employers</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#00C9A7] mt-0.5 flex-shrink-0" />
                <span>Keep your records up-to-date with current employment</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoreExplainerModal;
